import React, { useState, Component } from 'react';
import Layout from '../../components/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, SearchIcon, BrainIcon, FileTextIcon, BookOpenIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, ChevronRightIcon, PlusCircleIcon, HeartIcon, ArrowRightIcon, CalendarIcon, UploadIcon, SparklesIcon, AwardIcon, TrendingUpIcon, BriefcaseIcon, FilterIcon, BarChart3Icon, CheckIcon, XIcon, LightbulbIcon, BellIcon, CircleIcon, FolderIcon, ClipboardIcon, StarIcon, EyeIcon, LogOutIcon, MoreHorizontalIcon, XCircleIcon, PrinterIcon } from 'lucide-react';
import ExplorePrograms from './ExplorePrograms';
import EligibilityBadge, { EligibilityStatus } from '../../components/EligibilityBadge';
import ExemptionCheckCard from '../../components/ExemptionCheckCard';
// Sample data for the student dashboard
const studentData = {
  name: 'Sarah Ahmed',
  recommendations: [{
    id: 'PRG001',
    name: 'Master of Business Administration',
    type: "Master's",
    duration: '2 Years',
    matchScore: 95,
    skills: ['Leadership', 'Strategic Management', 'Finance'],
    tags: ['In-Demand', 'High Salary'],
    university: 'UAE Business School',
    deadline: 'March 15, 2024'
  }, {
    id: 'PRG002',
    name: 'Bachelor of Computer Science',
    type: "Bachelor's",
    duration: '4 Years',
    matchScore: 88,
    skills: ['Programming', 'Data Structures', 'Algorithms'],
    tags: ['High Completion', 'In-Demand'],
    university: 'UAE Technology Institute',
    deadline: 'April 30, 2024'
  }, {
    id: 'PRG003',
    name: 'Diploma in Digital Marketing',
    type: 'Diploma',
    duration: '1 Year',
    matchScore: 82,
    skills: ['Social Media Marketing', 'SEO', 'Content Strategy'],
    tags: ['Fast Completion', 'Flexible Schedule'],
    university: 'UAE Business School',
    deadline: 'May 20, 2024'
  }],
  applications: [{
    id: 'APP001',
    programId: 'PRG001',
    programName: 'Master of Business Administration',
    university: 'UAE Business School',
    status: 'In Progress',
    progress: 80,
    steps: [{
      name: 'Personal Information',
      completed: true
    }, {
      name: 'Academic History',
      completed: true
    }, {
      name: 'Documents',
      completed: false
    }, {
      name: 'Statement of Purpose',
      completed: true
    }, {
      name: 'Submission',
      completed: false
    }],
    lastUpdated: '2 days ago'
  }, {
    id: 'APP002',
    programId: 'PRG003',
    programName: 'Diploma in Digital Marketing',
    university: 'UAE Business School',
    status: 'Submitted',
    progress: 100,
    steps: [{
      name: 'Personal Information',
      completed: true
    }, {
      name: 'Academic History',
      completed: true
    }, {
      name: 'Documents',
      completed: true
    }, {
      name: 'Statement of Purpose',
      completed: true
    }, {
      name: 'Submission',
      completed: true
    }],
    lastUpdated: '1 week ago',
    decisionDate: 'Expected by June 15, 2024'
  }],
  documents: [{
    id: 'DOC001',
    name: 'Academic Transcript',
    type: 'PDF',
    status: 'Verified',
    uploadDate: 'May 1, 2024'
  }, {
    id: 'DOC002',
    name: 'Passport Copy',
    type: 'PDF',
    status: 'Verified',
    uploadDate: 'May 1, 2024'
  }, {
    id: 'DOC003',
    name: 'CV/Resume',
    type: 'PDF',
    status: 'Pending',
    uploadDate: 'May 3, 2024'
  }, {
    id: 'DOC004',
    name: 'IELTS Certificate',
    type: 'Missing',
    status: 'Missing',
    uploadDate: ''
  }],
  pendingTasks: [{
    id: 'TASK001',
    description: 'Upload your IELTS certificate to complete your MBA application',
    priority: 'High',
    applicationId: 'APP001'
  }, {
    id: 'TASK002',
    description: 'Review and sign the financial declaration form',
    priority: 'Medium',
    applicationId: 'APP001'
  }],
  insights: [{
    id: 'INSIGHT001',
    message: "You're eligible for 4 scholarships based on your profile",
    type: 'opportunity',
    cta: 'View Scholarships'
  }, {
    id: 'INSIGHT002',
    message: '3 new programs match your profile since last week',
    type: 'recommendation',
    cta: 'Explore Programs'
  }]
};
// Program Card Component
const ProgramCard = ({
  program,
  recommended = false
}) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const navigate = useNavigate();
  const getTagIcon = tag => {
    switch (tag) {
      case 'In-Demand':
        return <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-5469" />;
      case 'High Salary':
        return <BriefcaseIcon className="w-3 h-3 mr-1" data-id="element-5470" />;
      case 'High Completion':
        return <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-5471" />;
      case 'Fast Completion':
        return <ClockIcon className="w-3 h-3 mr-1" data-id="element-5472" />;
      case 'Flexible Schedule':
        return <CalendarIcon className="w-3 h-3 mr-1" data-id="element-5473" />;
      default:
        return <SparklesIcon className="w-3 h-3 mr-1" data-id="element-5474" />;
    }
  };
  // Determine eligibility status (in a real app, this would come from an API)
  const getEligibilityStatus = (): EligibilityStatus => {
    // For demo purposes, we'll assign statuses based on program ID
    const id = program.id;
    if (id === 'PRG001') return 'NEEDS_EXEMPTION';
    if (id === 'PRG002') return 'GREENLISTED';
    if (id === 'PRG003') return 'AUTO_REJECT';
    return 'GREENLISTED';
  };
  const getEligibilityReason = (status: EligibilityStatus): string => {
    switch (status) {
      case 'GREENLISTED':
        return 'This program is aligned with national priorities. No exemption is required.';
      case 'NEEDS_EXEMPTION':
        return 'This program requires an exemption because it falls outside the national priority fields of study.';
      case 'AUTO_REJECT':
        return 'This program is not eligible for sponsorship due to policy restrictions.';
      default:
        return '';
    }
  };
  const eligibilityStatus = getEligibilityStatus();
  const eligibilityReason = getEligibilityReason(eligibilityStatus);
  const handleStartExemption = e => {
    e.stopPropagation(); // Prevent card click
    navigate(`/student/exemptions/new?programId=${program.id}`);
  };
  // Handle clicking on the entire program card
  const handleProgramClick = () => {
    navigate(`/student/programs/${program.id}`);
  };
  return <div className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden cursor-pointer" onClick={handleProgramClick} data-id="element-5475">
      {recommended && <div className="bg-primary-500 text-white text-xs font-medium px-3 py-1.5" data-id="element-5476">
          <div className="relative inline-flex items-center cursor-pointer group" data-id="element-5477">
            <div className="hover:opacity-90 transition-opacity flex items-center" data-id="element-5478">
              <SparklesIcon className="w-3 h-3 mr-1.5" data-id="element-5479" />
              {program.matchScore}% Match
            </div>
            <div className="invisible group-hover:visible absolute bottom-full left-0 mb-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-sm z-[100] transform -translate-x-1/4" data-id="element-5480">
              <div className="flex items-start" data-id="element-5481">
                <div className="text-left" data-id="element-5482">
                  <h4 className="font-medium text-gray-900 mb-1" data-id="element-5483">
                    Why This Program Matches Your Profile
                  </h4>
                  <p className="text-gray-700 text-xs" data-id="element-5484">
                    {program.matchScore >= 90 ? `This program is an excellent match for your profile! Your background in ${program.skills[0]} and interest in ${program.skills[1] || program.skills[0]} aligns perfectly with this program's curriculum.` : program.matchScore >= 80 ? `This program is a great match for your profile. Your skills in ${program.skills[0]} complement this program's focus on ${program.tags[0]}.` : program.matchScore >= 70 ? `This program is a good match for your profile. Your interest in ${program.skills[0]} aligns with this program's offerings.` : `This program matches some aspects of your profile, particularly your interest in ${program.skills[0]}.`}
                    {program.tags.includes('High Salary') && ' This program can lead to high-paying career opportunities.'}
                    {program.tags.includes('In-Demand') && ' Graduates of this program are in high demand in the job market.'}
                    {program.tags.includes('Fast Completion') && ' You can complete this program relatively quickly.'}
                    {program.tags.includes('Flexible Schedule') && ' The program offers flexibility that matches your preferences.'}
                  </p>
                </div>
              </div>
              <div className="absolute bottom-0 left-8 transform translate-y-1/2 rotate-45 w-2 h-2 bg-white border-r border-b border-gray-200" data-id="element-5485"></div>
            </div>
          </div>
        </div>}
      <div className="p-5" data-id="element-5486">
        <div className="flex justify-between items-start mb-3" data-id="element-5487">
          <div data-id="element-5488">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-5489">
              {program.name}
            </h3>
            <div className="flex items-center mt-1" data-id="element-5490">
              <span className="text-sm text-gray-600 mr-2" data-id="element-5491">
                {program.university}
              </span>
              <EligibilityBadge status={eligibilityStatus} size="sm" reason={eligibilityReason} data-id="element-5492" />
            </div>
          </div>
          <button onClick={e => {
          e.stopPropagation(); // Prevent card click when clicking the heart
          setIsFavorite(!isFavorite);
        }} className="text-gray-400 hover:text-red-500 transition-colors p-1" data-id="element-5493">
            <HeartIcon className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} data-id="element-5494" />
          </button>
        </div>
        <div className="flex items-center mb-4 text-sm text-gray-600" data-id="element-5495">
          <GraduationCapIcon className="w-4 h-4 mr-1.5" data-id="element-5496" />
          <span className="mr-4" data-id="element-5497">{program.type}</span>
          <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-5498" />
          <span data-id="element-5499">{program.duration}</span>
        </div>
        <div className="mb-4" data-id="element-5500">
          <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-5501">
            {program.skills.map((skill, index) => <span key={index} className="bg-gray-50 text-gray-700 text-xs px-2.5 py-1 rounded-full" data-id="element-5502">
                {skill}
              </span>)}
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-5503">
            {program.tags.map((tag, index) => <span key={index} className="bg-primary-50 text-primary-700 text-xs px-2.5 py-1 rounded-full flex items-center" data-id="element-5504">
                {getTagIcon(tag)}
                {tag}
              </span>)}
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 text-sm" data-id="element-5505">
          <div className="text-gray-600" data-id="element-5506">
            <CalendarIcon className="w-4 h-4 inline mr-1.5" data-id="element-5507" />
            Deadline: {program.deadline}
          </div>
          {eligibilityStatus === 'NEEDS_EXEMPTION' && <button onClick={handleStartExemption} className="px-2 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 transition-colors" data-id="element-5508">
              Start Exemption
            </button>}
          {eligibilityStatus === 'GREENLISTED' && <button onClick={e => {
          e.stopPropagation();
          navigate(`/student/application/${program.id}`);
        }} className="px-2 py-1 bg-primary-500 text-white rounded text-xs font-medium hover:bg-primary-600 transition-colors" data-id="element-5509">
              Apply Now
            </button>}
        </div>
      </div>
    </div>;
};
// Application Card Component
const ApplicationCard = ({
  application
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const getStatusColor = status => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue-50 text-blue-700';
      case 'Under Review':
        return 'bg-purple-50 text-purple-700';
      case 'Accepted':
        return 'bg-green-50 text-green-700';
      case 'Rejected':
        return 'bg-red-50 text-red-700';
      case 'In Progress':
        return 'bg-yellow-50 text-yellow-700';
      case 'Awaiting Documents':
        return 'bg-orange-50 text-orange-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };
  const getStatusIcon = status => {
    switch (status) {
      case 'Submitted':
        return <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-5510" />;
      case 'Under Review':
        return <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-5511" />;
      case 'Accepted':
        return <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-5512" />;
      case 'Rejected':
        return <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-5513" />;
      case 'In Progress':
        return <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-5514" />;
      case 'Awaiting Documents':
        return <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-5515" />;
      default:
        return <CircleIcon className="w-4 h-4 mr-1.5" data-id="element-5516" />;
    }
  };
  // Determine application timeline steps
  const getTimelineSteps = application => {
    const baseSteps = [{
      name: 'Submitted',
      completed: true
    }, {
      name: 'Under Review',
      completed: application.status !== 'Submitted' && application.status !== 'In Progress'
    }, {
      name: 'Decision',
      completed: application.status === 'Accepted' || application.status === 'Rejected'
    }];
    return baseSteps;
  };
  // Get next action based on application status
  const getNextAction = application => {
    switch (application.status) {
      case 'In Progress':
        return {
          label: 'Continue Application',
          action: 'continue',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'Awaiting Documents':
        return {
          label: 'Upload Documents',
          action: 'upload',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'Submitted':
        return {
          label: 'View Application',
          action: 'view',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'Under Review':
        return {
          label: 'Track Status',
          action: 'track',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'Accepted':
        return {
          label: 'Next Steps',
          action: 'next-steps',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'Rejected':
        return {
          label: 'View Feedback',
          action: 'feedback',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      default:
        return {
          label: 'View Details',
          action: 'view',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
    }
  };
  const handleActionClick = action => {
    switch (action) {
      case 'continue':
        navigate(`/student/application/${application.programId}`);
        break;
      case 'upload':
        navigate(`/student/documents`);
        break;
      case 'view':
      case 'track':
        navigate(`/student/applications-and-scholarships?tab=applications`);
        break;
      case 'next-steps':
      case 'feedback':
        navigate(`/student/applications-and-scholarships?tab=applications`);
        break;
      default:
        navigate(`/student/applications-and-scholarships?tab=applications`);
    }
  };
  const timelineSteps = getTimelineSteps(application);
  const nextAction = getNextAction(application);
  // Calculate progress bar width based on status
  const getProgressWidth = status => {
    switch (status) {
      case 'In Progress':
        return '10%';
      case 'Submitted':
        return '33%';
      case 'Under Review':
      case 'Awaiting Documents':
        return '66%';
      case 'Accepted':
      case 'Rejected':
        return '100%';
      default:
        return '0%';
    }
  };
  // Toggle menu open/closed
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  return <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all" data-id="element-5517">
      <div className="p-5" data-id="element-5518">
        <div className="flex justify-between items-start mb-3" data-id="element-5519">
          <div data-id="element-5520">
            <h3 className="text-lg font-medium text-gray-800" data-id="element-5521">
              {application.programName}
            </h3>
            <p className="text-sm text-gray-600 mt-1" data-id="element-5522">
              {application.university}
            </p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center ${getStatusColor(application.status)}`} data-id="element-5523">
            {getStatusIcon(application.status)}
            {application.status}
          </span>
        </div>
        {/* Application Timeline */}
        <div className="my-4" data-id="element-5524">
          <div className="flex items-center justify-between mb-2" data-id="element-5525">
            {timelineSteps.map((step, index) => <div key={index} className="flex flex-col items-center" data-id="element-5526">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step.completed ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-5527">
                  {step.completed ? <CheckIcon className="w-3.5 h-3.5" data-id="element-5528" /> : <span className="text-xs" data-id="element-5529">{index + 1}</span>}
                </div>
                <span className={`text-xs mt-1 ${step.completed ? 'text-primary-600 font-medium' : 'text-gray-500'}`} data-id="element-5530">
                  {step.name}
                </span>
              </div>)}
          </div>
          <div className="relative h-1 w-full bg-gray-200 rounded-full mt-1 mb-3" data-id="element-5531">
            <div className="absolute h-1 bg-primary-500 rounded-full" style={{
            width: getProgressWidth(application.status)
          }} data-id="element-5532"></div>
          </div>
        </div>
        {/* Important Metadata */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm" data-id="element-5533">
          <div data-id="element-5534">
            <p className="text-xs text-gray-500 mb-1" data-id="element-5535">Last Updated</p>
            <p className="text-gray-700" data-id="element-5536">{application.lastUpdated}</p>
          </div>
          {application.decisionDate && <div data-id="element-5537">
              <p className="text-xs text-gray-500 mb-1" data-id="element-5538">Decision Expected</p>
              <p className="text-gray-700" data-id="element-5539">{application.decisionDate}</p>
            </div>}
        </div>
        {/* Application Alerts */}
        {application.status === 'Awaiting Documents' && <div className="bg-orange-50 border border-orange-100 rounded-md p-3 mb-4" data-id="element-5540">
            <div className="flex items-start" data-id="element-5541">
              <AlertCircleIcon className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5542" />
              <div data-id="element-5543">
                <p className="text-sm text-orange-800 font-medium" data-id="element-5544">
                  Action Required
                </p>
                <p className="text-xs text-orange-700 mt-0.5" data-id="element-5545">
                  Your application is missing required documents. Please upload
                  them as soon as possible.
                </p>
              </div>
            </div>
          </div>}
        {application.status === 'Accepted' && <div className="bg-green-50 border border-green-100 rounded-md p-3 mb-4" data-id="element-5546">
            <div className="flex items-start" data-id="element-5547">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5548" />
              <div data-id="element-5549">
                <p className="text-sm text-green-800 font-medium" data-id="element-5550">
                  Congratulations!
                </p>
                <p className="text-xs text-green-700 mt-0.5" data-id="element-5551">
                  Your application has been accepted. Please review the next
                  steps.
                </p>
              </div>
            </div>
          </div>}
        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-4" data-id="element-5552">
          <button onClick={() => handleActionClick(nextAction.action)} className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors ${nextAction.color}`} data-id="element-5553">
            {nextAction.label}
          </button>
          <div className="relative" data-id="element-5554">
            <button className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100" onClick={toggleMenu} data-id="element-5555">
              <MoreHorizontalIcon className="w-5 h-5" data-id="element-5556" />
            </button>
            {menuOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-5557">
                <div className="py-1" data-id="element-5558">
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-5559">
                    <EyeIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5560" />
                    View Details
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-5561">
                    <FileTextIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5562" />
                    View Documents
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-5563">
                    <PrinterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5564" />
                    Print Application
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-5565">
                    <XCircleIcon className="w-4 h-4 mr-2 text-red-500" data-id="element-5566" />
                    Withdraw Application
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
// AI Companion Component (combines Insights and Tasks)
const AICompanion = ({
  insights,
  tasks
}) => {
  const navigate = useNavigate();
  const handleInsightAction = insight => {
    if (insight.cta === 'View Scholarships') {
      navigate('/student/scholarships');
    } else if (insight.cta === 'Explore Programs') {
      navigate('/student/programs');
    }
  };
  const handleTaskAction = task => {
    if (task.applicationId) {
      navigate(`/student/application/${task.applicationId}`);
    } else {
      navigate('/student/documents');
    }
  };
  return <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-5567">
      <div className="p-4 border-b border-gray-100" data-id="element-5568">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-5569">
          <SparklesIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-5570" />
          Your AI Companion
        </h2>
      </div>
      {/* Actions Section */}
      <div className="p-5" data-id="element-5571">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center" data-id="element-5572">
          <ClipboardIcon className="w-4 h-4 mr-1.5 text-primary-400" data-id="element-5573" />
          Actions
        </h3>
        {tasks.length > 0 ? <div className="space-y-3 mb-6" data-id="element-5574">
            {tasks.map(task => <div key={task.id} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors" data-id="element-5575">
                <div className="flex-shrink-0 mt-0.5" data-id="element-5576">
                  {task.priority === 'High' ? <AlertCircleIcon className="w-5 h-5 text-red-500" data-id="element-5577" /> : <BellIcon className="w-5 h-5 text-yellow-500" data-id="element-5578" />}
                </div>
                <div className="ml-3 flex-grow" data-id="element-5579">
                  <p className="text-sm text-gray-700" data-id="element-5580">{task.description}</p>
                  <button onClick={() => handleTaskAction(task)} className="mt-2 text-sm text-primary-600 font-medium hover:underline flex items-center" data-id="element-5581">
                    Complete Now
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-5582" />
                  </button>
                </div>
              </div>)}
          </div> : <div className="text-center py-5 mb-6" data-id="element-5583">
            <CheckCircleIcon className="w-8 h-8 text-green-500 mx-auto mb-2" data-id="element-5584" />
            <p className="text-gray-600" data-id="element-5585">
              You're all caught up! No pending tasks.
            </p>
          </div>}
        {/* Insights Section */}
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 flex items-center" data-id="element-5586">
          <LightbulbIcon className="w-4 h-4 mr-1.5 text-primary-400" data-id="element-5587" />
          Insights
        </h3>
        <div className="space-y-3" data-id="element-5588">
          {insights.map(insight => <div key={insight.id} className={`p-3 rounded-lg border ${insight.type === 'opportunity' ? 'bg-green-50 border-green-100 text-green-800' : 'bg-blue-50 border-blue-100 text-blue-800'}`} data-id="element-5589">
              <div className="flex items-start" data-id="element-5590">
                <SparklesIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-5591" />
                <div className="flex-grow" data-id="element-5592">
                  <p className="text-sm mb-2" data-id="element-5593">{insight.message}</p>
                  <button onClick={() => handleInsightAction(insight)} className="text-sm font-medium hover:underline flex items-center" data-id="element-5594">
                    {insight.cta}
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-5595" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
// Documents Component
const DocumentsSection = ({
  documents
}) => {
  // Group documents by status
  const documentsByStatus = documents.reduce((acc, doc) => {
    const status = doc.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(doc);
    return acc;
  }, {});
  const getStatusIcon = status => {
    switch (status) {
      case 'Verified':
        return <CircleIcon className="w-2 h-2 text-green-500" data-id="element-5596" />;
      case 'Pending':
        return <CircleIcon className="w-2 h-2 text-yellow-500" data-id="element-5597" />;
      case 'Missing':
        return <CircleIcon className="w-2 h-2 text-red-500" data-id="element-5598" />;
      default:
        return <CircleIcon className="w-2 h-2 text-gray-500" data-id="element-5599" />;
    }
  };
  return <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-5600">
      <div className="flex justify-between items-center p-4 border-b border-gray-100" data-id="element-5601">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-5602">
          <FolderIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-5603" />
          My Documents
        </h2>
        <button className="text-primary-600 text-sm font-medium hover:text-primary-700 flex items-center" data-id="element-5604">
          Manage
          <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-5605" />
        </button>
      </div>
      <div className="p-5" data-id="element-5606">
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center mb-5 hover:border-primary-400 transition-colors cursor-pointer" data-id="element-5607">
          <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-5608" />
          <p className="text-sm text-gray-600 mb-1" data-id="element-5609">Drag and drop files here</p>
          <p className="text-xs text-gray-500 mb-3" data-id="element-5610">or</p>
          <button className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors" data-id="element-5611">
            Browse Files
          </button>
        </div>
        {/* Document groups by status */}
        {Object.keys(documentsByStatus).length > 0 && <div className="space-y-4" data-id="element-5612">
            {Object.entries(documentsByStatus).map(([status, docs]) => <div key={status} data-id="element-5613">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-5614">
                  {getStatusIcon(status)}
                  <span className="ml-2" data-id="element-5615">{status} Documents</span>
                  <span className="ml-1 text-gray-500" data-id="element-5616">({docs.length})</span>
                </h3>
                <div className="space-y-2" data-id="element-5617">
                  {docs.map(doc => <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5618">
                      <div className="flex items-center" data-id="element-5619">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3" data-id="element-5620">
                          <FileTextIcon className="w-4 h-4 text-gray-600" data-id="element-5621" />
                        </div>
                        <div data-id="element-5622">
                          <p className="text-sm font-medium text-gray-800" data-id="element-5623">
                            {doc.name}
                          </p>
                          {doc.status !== 'Missing' && <p className="text-xs text-gray-500" data-id="element-5624">
                              {doc.uploadDate}
                            </p>}
                        </div>
                      </div>
                      {status === 'Missing' ? <button className="text-xs px-2.5 py-1 bg-red-50 text-red-600 rounded-full font-medium hover:bg-red-100 transition-colors" data-id="element-5625">
                          Upload
                        </button> : <button className="text-gray-500 hover:text-primary-600 p-1 rounded-full hover:bg-gray-100" data-id="element-5626">
                          <EyeIcon className="w-4 h-4" data-id="element-5627" />
                        </button>}
                    </div>)}
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
};
// Main Student Dashboard Component
const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [applicationTab, setApplicationTab] = useState('all');
  // Check if the student has any active exemption requests
  const hasActiveExemptionRequests = false; // In a real app, this would be determined from the user's data
  // Check if we're on the programs page
  const isExplorePrograms = location.pathname === '/student/programs';
  // Add the missing handleProgramClick function
  const handleProgramClick = programId => {
    navigate(`/student/programs/${programId}`);
  };
  if (isExplorePrograms) {
    return <ExplorePrograms data-id="element-5628" />;
  }
  return <Layout persona="Student" data-id="element-5629">
      <div className="max-w-7xl mx-auto" data-id="element-5630">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 mb-8" data-id="element-5631">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5632">
            <div data-id="element-5633">
              <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-5634">
                Welcome, {studentData.name}
              </h1>
              <p className="text-gray-600 mb-4 md:mb-0" data-id="element-5635">
                Programs, scholarships, exemptions — apply for everything in one place.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-5636">
              <button onClick={() => navigate('/student/programs')} className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center justify-center" data-id="element-5637">
                <SearchIcon className="w-4 h-4 mr-2" data-id="element-5638" />
                Explore Programs
              </button>
              
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5639">
          {/* Left Column - Applications & Programs */}
          <div className="lg:col-span-2 space-y-8" data-id="element-5640">
            {/* My Applications Section */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-5641">
              <div className="flex justify-between items-center p-4 border-b border-gray-100" data-id="element-5642">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-5643">
                  <ClipboardIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-5644" />
                  My Applications
                </h2>
                <button className="text-primary-600 text-sm font-medium hover:text-primary-700 flex items-center" data-id="element-5645">
                  View All
                  <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-5646" />
                </button>
              </div>
              {/* Application Status Tabs */}
              <div className="border-b border-gray-100" data-id="element-5647">
                <div className="flex overflow-x-auto scrollbar-hide" data-id="element-5648">
                  <button onClick={() => setApplicationTab('all')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${applicationTab === 'all' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5649">
                    All Applications ({studentData.applications.length})
                  </button>
                  <button onClick={() => setApplicationTab('submitted')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${applicationTab === 'submitted' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5650">
                    Submitted (
                    {studentData.applications.filter(app => app.status === 'Submitted' || app.status === 'Under Review' || app.status === 'Awaiting Documents').length}
                    )
                  </button>
                  <button onClick={() => setApplicationTab('accepted')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${applicationTab === 'accepted' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5651">
                    Accepted (
                    {studentData.applications.filter(app => app.status === 'Accepted').length}
                    )
                  </button>
                  <button onClick={() => setApplicationTab('inProgress')} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${applicationTab === 'inProgress' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-5652">
                    In Progress (
                    {studentData.applications.filter(app => app.status === 'In Progress').length}
                    )
                  </button>
                </div>
              </div>
              <div className="p-5 space-y-5" data-id="element-5653">
                {studentData.applications.length > 0 ? <>
                    {/* Display applications based on selected tab */}
                    {applicationTab === 'all' && studentData.applications.map(application => <ApplicationCard key={application.id} application={application} data-id="element-5654" />)}
                    {applicationTab === 'submitted' && studentData.applications.filter(app => app.status === 'Submitted' || app.status === 'Under Review' || app.status === 'Awaiting Documents').map(application => <ApplicationCard key={application.id} application={application} data-id="element-5655" />)}
                    {applicationTab === 'accepted' && studentData.applications.filter(app => app.status === 'Accepted').map(application => <ApplicationCard key={application.id} application={application} data-id="element-5656" />)}
                    {applicationTab === 'inProgress' && studentData.applications.filter(app => app.status === 'In Progress').map(application => <ApplicationCard key={application.id} application={application} data-id="element-5657" />)}
                    {applicationTab !== 'all' && (applicationTab === 'submitted' && studentData.applications.filter(app => app.status === 'Submitted' || app.status === 'Under Review' || app.status === 'Awaiting Documents').length === 0 || applicationTab === 'accepted' && studentData.applications.filter(app => app.status === 'Accepted').length === 0 || applicationTab === 'inProgress' && studentData.applications.filter(app => app.status === 'In Progress').length === 0) && <div className="text-center py-8" data-id="element-5658">
                          <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-5659">
                            <ClipboardIcon className="w-6 h-6 text-gray-400" data-id="element-5660" />
                          </div>
                          <h3 className="text-gray-500 font-medium mb-1" data-id="element-5661">
                            No applications in this category
                          </h3>
                          <p className="text-gray-400 text-sm" data-id="element-5662">
                            {applicationTab === 'inProgress' ? 'Start a new application to see it here' : applicationTab === 'submitted' ? 'Submit an application to see it here' : "When your applications are accepted, they'll appear here"}
                          </p>
                        </div>}
                  </> : <div className="text-center py-10" data-id="element-5663">
                    <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-5664">
                      <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-5665" />
                    </div>
                    <h3 className="text-gray-700 font-medium mb-2" data-id="element-5666">
                      No applications yet
                    </h3>
                    <p className="text-gray-500 text-sm max-w-md mx-auto mb-5" data-id="element-5667">
                      Start your journey by exploring programs and applying to
                      ones that match your interests and qualifications.
                    </p>
                    <button className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center" data-id="element-5668">
                      <SearchIcon className="w-4 h-4 mr-2" data-id="element-5669" />
                      Explore Programs
                    </button>
                  </div>}
              </div>
            </div>
            {/* Exemption Check Card - Only show if the student has no active exemption requests */}
            {!hasActiveExemptionRequests && <ExemptionCheckCard data-id="element-5670" />}
            {/* Recommended Programs Section */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-id="element-5671">
              <div className="flex justify-between items-center p-4 border-b border-gray-100" data-id="element-5672">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-5673">
                  <StarIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-5674" />
                  Recommended Programs
                </h2>
                <div className="flex items-center space-x-2" data-id="element-5675">
                  <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-5676">
                    <FilterIcon className="w-5 h-5" data-id="element-5677" />
                  </button>
                  <button className="text-primary-600 text-sm font-medium hover:text-primary-700 flex items-center" data-id="element-5678">
                    View All
                    <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-5679" />
                  </button>
                </div>
              </div>
              <div className="p-5 grid grid-cols-1 gap-5" data-id="element-5680">
                {studentData.recommendations.slice(0, 2).map(program => <div key={program.id} onClick={() => handleProgramClick(program.id)} className="cursor-pointer" data-id="element-5681">
                    <ProgramCard program={program} recommended={true} data-id="element-5682" />
                  </div>)}
                <button className="w-full py-4 border border-dashed border-gray-200 rounded-lg text-gray-600 hover:text-primary-600 hover:border-primary-500 transition-colors flex items-center justify-center" data-id="element-5683">
                  <PlusCircleIcon className="w-5 h-5 mr-2" data-id="element-5684" />
                  Show More Programs
                </button>
              </div>
            </div>
          </div>
          {/* Right Column - AI Companion & Documents */}
          <div className="space-y-8" data-id="element-5685">
            {/* AI Companion (combined Insights & Tasks) */}
            <AICompanion insights={studentData.insights} tasks={studentData.pendingTasks} data-id="element-5686" />
            {/* Documents Section */}
            <DocumentsSection documents={studentData.documents} data-id="element-5687" />
          </div>
        </div>
      </div>
    </Layout>;
};
// Student Navbar Component (updated)
const StudentNavbar = ({
  navItems,
  isActive,
  navigate
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const notifications = [{
    id: 1,
    message: 'Deadline for MBA application is in 3 days',
    type: 'deadline'
  }, {
    id: 2,
    message: 'Your IELTS certificate is missing',
    type: 'document'
  }, {
    id: 3,
    message: 'New program matches your profile',
    type: 'recommendation'
  }];
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setProfileOpen(false);
  };
  const toggleProfile = () => {
    setProfileOpen(!profileOpen);
    setNotificationsOpen(false);
  };
  const handleSwitchPersona = () => {
    navigate('/');
  };
  const getNotificationIcon = type => {
    switch (type) {
      case 'deadline':
        return <CalendarIcon className="w-4 h-4 text-yellow-500" data-id="element-5688" />;
      case 'document':
        return <FileTextIcon className="w-4 h-4 text-red-500" data-id="element-5689" />;
      case 'recommendation':
        return <GraduationCapIcon className="w-4 h-4 text-green-500" data-id="element-5690" />;
      default:
        return <BellIcon className="w-4 h-4 text-primary-500" data-id="element-5691" />;
    }
  };
  return <>
      {/* Desktop Navigation */}
      <div className="bg-white shadow-sm sticky top-0 z-50" data-id="element-5692">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5693">
          <div className="flex justify-between h-16" data-id="element-5694">
            {/* Logo and Brand */}
            <div className="flex items-center" data-id="element-5695">
              <div className="flex-shrink-0 flex items-center" data-id="element-5696">
                <div className="h-8 w-8 bg-primary-500 rounded-md flex items-center justify-center text-white font-bold" data-id="element-5697">
                  WS
                </div>
                <span className="ml-2 text-lg font-semibold text-gray-800" data-id="element-5698">
                  Career Navigator
                </span>
              </div>
            </div>
            {/* Desktop Nav Links */}
            <div className="hidden md:flex md:items-center md:space-x-4" data-id="element-5699">
              {navItems.map((item, index) => <a key={index} href={item.path} onClick={e => {
              e.preventDefault();
              navigate(item.path);
            }} className={`px-4 py-2 text-sm font-medium transition-colors ${isActive(item.path) ? 'text-primary-500 border-b-2 border-primary-500' : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'}`} data-id="element-5700">
                  <div className="flex items-center" data-id="element-5701">
                    {item.label}
                    {item.badge && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800" data-id="element-5702">
                        {item.badge}
                      </span>}
                  </div>
                </a>)}
            </div>
            {/* Right side icons */}
            <div className="flex items-center" data-id="element-5703">
              {/* Notifications */}
              <div className="relative ml-3" data-id="element-5704">
                <button onClick={toggleNotifications} className="p-1 rounded-full text-gray-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-5705">
                  <span className="sr-only" data-id="element-5706">View notifications</span>
                  <div className="relative" data-id="element-5707">
                    <BellIcon className="h-6 w-6" data-id="element-5708" />
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-xs text-white" data-id="element-5709">
                      3
                    </span>
                  </div>
                </button>
                {/* Notifications dropdown */}
                {notificationsOpen && <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none" data-id="element-5710">
                    <div className="py-1" data-id="element-5711">
                      <div className="px-4 py-2 border-b border-gray-100" data-id="element-5712">
                        <h3 className="text-sm font-medium text-gray-700" data-id="element-5713">
                          Notifications
                        </h3>
                      </div>
                      {notifications.map(notification => <a key={notification.id} href="#" className="block px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0" data-id="element-5714">
                          <div className="flex items-start" data-id="element-5715">
                            <div className="flex-shrink-0 mt-0.5" data-id="element-5716">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="ml-3" data-id="element-5717">
                              <p className="text-sm text-gray-700" data-id="element-5718">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-5719">
                                5 minutes ago
                              </p>
                            </div>
                          </div>
                        </a>)}
                      <div className="px-4 py-2 text-center border-t border-gray-100" data-id="element-5720">
                        <a href="#" className="text-sm font-medium text-primary-500 hover:text-primary-600" data-id="element-5721">
                          View all notifications
                        </a>
                      </div>
                    </div>
                  </div>}
              </div>
              {/* Profile dropdown */}
              <div className="relative ml-3" data-id="element-5722">
                <button onClick={toggleProfile} className="flex items-center max-w-xs rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-5723">
                  <span className="sr-only" data-id="element-5724">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white" data-id="element-5725">
                    SA
                  </div>
                  <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500" data-id="element-5726" />
                </button>
                {profileOpen && <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none" data-id="element-5727">
                    <div className="py-1" data-id="element-5728">
                      <button onClick={handleSwitchPersona} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-5729">
                        <LogOutIcon className="w-4 h-4 mr-2" data-id="element-5730" />
                        Switch Persona
                      </button>
                    </div>
                  </div>}
              </div>
              {/* Mobile menu button */}
              <div className="ml-2 -mr-2 flex items-center md:hidden" data-id="element-5731">
                <button onClick={toggleMobileMenu} className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-primary-500 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500" data-id="element-5732">
                  <span className="sr-only" data-id="element-5733">Open main menu</span>
                  {mobileMenuOpen ? <XIcon className="block h-6 w-6" data-id="element-5734" /> : <MenuIcon className="block h-6 w-6" data-id="element-5735" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile menu, show/hide based on menu state */}
        {mobileMenuOpen && <div className="md:hidden" data-id="element-5736">
            <div className="pt-2 pb-3 space-y-1 border-t border-gray-200" data-id="element-5737">
              {navItems.map((item, index) => <a key={index} href={item.path} onClick={e => {
            e.preventDefault();
            navigate(item.path);
            setMobileMenuOpen(false);
          }} className={`w-full flex items-center px-4 py-3 text-lg ${isActive(item.path) ? 'bg-primary-50 text-primary-500 font-medium' : 'text-gray-600 hover:bg-primary-50 hover:text-primary-600'}`} data-id="element-5738">
                  <span className="mr-3" data-id="element-5739">{item.icon}</span>
                  {item.label}
                  {item.badge && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800" data-id="element-5740">
                      {item.badge}
                    </span>}
                </a>)}
            </div>
            <div className="pt-4 pb-3 border-t border-gray-200" data-id="element-5741">
              <div className="flex items-center px-4" data-id="element-5742">
                <div className="flex-shrink-0" data-id="element-5743">
                  <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center text-white" data-id="element-5744">
                    SA
                  </div>
                </div>
                <div className="ml-3" data-id="element-5745">
                  <div className="text-base font-medium text-gray-800" data-id="element-5746">
                    Sarah Ahmed
                  </div>
                  <div className="text-sm font-medium text-gray-500" data-id="element-5747">
                    sarah@example.com
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-1" data-id="element-5748">
                <button onClick={handleSwitchPersona} className="block w-full text-left px-4 py-2 text-base font-medium text-gray-600 hover:text-primary-500 hover:bg-primary-50 flex items-center" data-id="element-5749">
                  <LogOutIcon className="w-5 h-5 mr-2" data-id="element-5750" />
                  Switch Persona
                </button>
              </div>
            </div>
          </div>}
      </div>
      {/* Mobile floating CTA */}
      <div className="fixed bottom-6 right-6 md:hidden z-40" data-id="element-5751">
        <button className="h-14 w-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center hover:bg-primary-600 transition-colors" data-id="element-5752">
          <PlusCircleIcon className="h-6 w-6" data-id="element-5753" />
        </button>
      </div>
    </>;
};
export default StudentDashboard;